import { TrendingUp, TrendingDown, Minus, Target, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { DeepAnalysis } from '@/hooks/useDeepAnalysis';

interface VerdictCardProps {
  analysis: DeepAnalysis;
}

const getVerdictConfig = (verdict: string) => {
  switch (verdict) {
    case 'STRONG_BUY':
      return { 
        color: 'bg-success text-success-foreground', 
        icon: TrendingUp,
        label: 'Strong Buy',
        gradient: 'from-success/20 to-success/5'
      };
    case 'BUY':
      return { 
        color: 'bg-success/80 text-success-foreground', 
        icon: TrendingUp,
        label: 'Buy',
        gradient: 'from-success/15 to-success/5'
      };
    case 'HOLD':
      return { 
        color: 'bg-warning text-warning-foreground', 
        icon: Minus,
        label: 'Hold',
        gradient: 'from-warning/15 to-warning/5'
      };
    case 'WATCH':
      return { 
        color: 'bg-primary text-primary-foreground', 
        icon: Minus,
        label: 'Watch',
        gradient: 'from-primary/15 to-primary/5'
      };
    case 'ACCUMULATE':
      return { 
        color: 'bg-success text-success-foreground', 
        icon: TrendingUp,
        label: 'Accumulate',
        gradient: 'from-success/20 to-success/5'
      };
    case 'SELL':
      return { 
        color: 'bg-destructive/80 text-destructive-foreground', 
        icon: TrendingDown,
        label: 'Sell',
        gradient: 'from-destructive/15 to-destructive/5'
      };
    case 'STRONG_SELL':
      return { 
        color: 'bg-destructive text-destructive-foreground', 
        icon: TrendingDown,
        label: 'Strong Sell',
        gradient: 'from-destructive/20 to-destructive/5'
      };
    default:
      return { 
        color: 'bg-muted text-muted-foreground', 
        icon: Minus,
        label: 'Unknown',
        gradient: 'from-muted/15 to-muted/5'
      };
  }
};

const formatAud = (value: unknown) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 'A$—';
  if (Math.abs(numberValue) < 0.01) return `A$${numberValue.toFixed(6)}`;
  if (Math.abs(numberValue) < 1) return `A$${numberValue.toFixed(4)}`;
  return `A$${numberValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export function VerdictCard({ analysis }: VerdictCardProps) {
  const verdictConfig = getVerdictConfig(analysis.verdict || 'WATCH');
  const VerdictIcon = verdictConfig.icon;
  const score = analysis.overallScore ?? 0;
  const confidence = analysis.confidence ?? 0;
  const priceTarget = analysis.priceTarget || { bearish: null, base: null, bullish: null };

  return (
    <Card className={`bg-gradient-to-br ${verdictConfig.gradient} border-2`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Review Signal</CardTitle>
          <Badge className={verdictConfig.color}>
            <VerdictIcon className="w-4 h-4 mr-1" />
            {verdictConfig.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Overall Score</span>
            <span className="font-bold text-foreground">{score}/100</span>
          </div>
          <Progress value={score} className="h-3" />
        </div>

        {/* Confidence */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">AI Confidence</span>
            <span className="font-bold text-foreground">{confidence}%</span>
          </div>
          <Progress value={confidence} className="h-2" />
        </div>

        {analysis.historicalAvailability?.partial && (
          <div className="rounded border border-border bg-secondary/30 p-2 text-xs text-muted-foreground">
            {analysis.historicalAvailability.notice || 'Partial analytics shown while live historical candles are unavailable.'}
          </div>
        )}

        {/* Executive Summary */}
        <div className="pt-2 border-t border-border">
          <p className="text-sm text-foreground leading-relaxed">
            {analysis.executiveSummary}
          </p>
        </div>

        {/* Price Targets */}
        <div className="grid grid-cols-3 gap-2 pt-2">
          <div className="text-center p-2 bg-destructive/10 rounded-lg">
            <Target className="w-4 h-4 mx-auto mb-1 text-destructive" />
            <p className="text-xs text-muted-foreground">Bearish</p>
            <p className="text-sm font-bold text-destructive">{formatAud(priceTarget.bearish)}</p>
          </div>
          <div className="text-center p-2 bg-warning/10 rounded-lg">
            <Target className="w-4 h-4 mx-auto mb-1 text-warning" />
            <p className="text-xs text-muted-foreground">Base</p>
            <p className="text-sm font-bold text-warning">{formatAud(priceTarget.base)}</p>
          </div>
          <div className="text-center p-2 bg-success/10 rounded-lg">
            <Target className="w-4 h-4 mx-auto mb-1 text-success" />
            <p className="text-xs text-muted-foreground">Bullish</p>
            <p className="text-sm font-bold text-success">{formatAud(priceTarget.bullish)}</p>
          </div>
        </div>

        {/* Timeframe */}
        <div className="flex items-center justify-center gap-2 pt-2">
          <AlertTriangle className="w-4 h-4 text-warning" />
          <span className="text-xs text-muted-foreground">
            Analysis Timeframe: <strong className="text-foreground">{analysis.timeframe}</strong>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
